import type { RingBuffer } from './ringBuffer'
import type { NetworkEntry } from '../types'
import { scrubCredentials } from './scrub'

export function installFetchPatch(buffer: RingBuffer<NetworkEntry>, sanitize: (url: string) => string): () => void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return () => {}
  const original = window.fetch.bind(window)
  window.fetch = async function patched(input: RequestInfo | URL, init?: RequestInit) {
    const start = performance.now()
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase()
    try {
      const response = await original(input, init)
      buffer.push({ url: sanitize(url), method, status: response.status, durationMs: Math.round(performance.now() - start), ts: Date.now() })
      return response
    } catch (err) {
      // Error messages from fetch (CORS rejection, abort, etc.) sometimes
      // echo the requested URL back including its full query string. Scrub
      // for the same patterns the console path does.
      const rawErr = err instanceof Error ? err.message : String(err)
      buffer.push({
        url: sanitize(url), method, status: 0,
        durationMs: Math.round(performance.now() - start),
        ts: Date.now(),
        error: scrubCredentials(rawErr),
      })
      throw err
    }
  }
  return () => { window.fetch = original }
}

export function installXhrPatch(buffer: RingBuffer<NetworkEntry>, sanitize: (url: string) => string): () => void {
  if (typeof window === 'undefined' || typeof window.XMLHttpRequest !== 'function') return () => {}
  const Original = window.XMLHttpRequest
  const originalOpen = Original.prototype.open
  const originalSend = Original.prototype.send

  Original.prototype.open = function patchedOpen(this: XMLHttpRequest & { __mfb?: { method: string; url: string; start: number } }, method: string, url: string | URL) {
    this.__mfb = { method: method.toUpperCase(), url: typeof url === 'string' ? url : url.toString(), start: performance.now() }
    return originalOpen.apply(this, arguments as unknown as Parameters<typeof originalOpen>)
  }

  Original.prototype.send = function patchedSend(this: XMLHttpRequest & { __mfb?: { method: string; url: string; start: number } }, body?: Document | XMLHttpRequestBodyInit | null) {
    this.addEventListener('loadend', () => {
      try {
        const ctx = this.__mfb
        if (!ctx) return
        buffer.push({
          url: sanitize(ctx.url),
          method: ctx.method,
          status: this.status,
          durationMs: Math.round(performance.now() - ctx.start),
          ts: Date.now(),
        })
      } catch { /* noop */ }
    })
    return originalSend.call(this, body ?? null)
  }

  return () => {
    Original.prototype.open = originalOpen
    Original.prototype.send = originalSend
  }
}
